import type { LucideProps } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import { SpinnerIcon } from "@/components/ui/SpinnerIcon"
import { defaultTTSProvider, doSpeak } from "./tts"
import type { LangCode, TTSProvider } from "./tts/types"

interface ISpeakerIconProps extends LucideProps {
  divRef?: React.Ref<HTMLDivElement>
  provider?: TTSProvider
  lang: LangCode
  voice?: string
  rate?: number
  volume?: number
  text?: string
  autoPlay?: boolean
}

export function SpeakerIcon({
  divRef,
  provider = defaultTTSProvider,
  text,
  lang,
  voice,
  rate,
  volume,
  autoPlay = false,
}: ISpeakerIconProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [speakingIconIndex] = useState(0)
  const stopRef = useRef<() => void>()
  const intervalRef = useRef<NodeJS.Timeout>()
  const autoPlayHandledRef = useRef(false)

  useEffect(() => {
    return () => {
      stopRef.current?.()
      const interval = intervalRef.current;
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [])

  const handleSpeak = useCallback(() => {
    if (!text) {
      return
    }
    console.debug("provider", provider, "lang", lang, "voice", voice)
    setIsLoading(true)
    setIsSpeaking(true)
    const controller = new AbortController()
    const { signal } = controller
    stopRef.current = () => {
      controller.abort()
      setIsSpeaking(false)
      setIsLoading(false)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
    doSpeak({
      provider,
      lang,
      text,
      voice,
      volume: volume,
      rate: rate,
      onFinish: () => {
        setIsSpeaking(false)
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
      },
      onStartSpeaking: () => {
        setIsLoading(false)
      },
      signal
    })

    // intervalRef.current = setInterval(() => {
    //     setSpeakingIconIndex((prevIndex) => (prevIndex + 1) % 3)
    // }, 300)
  }, [lang, provider, rate, text, voice, volume])

  useEffect(() => {
    if (autoPlay && text && !autoPlayHandledRef.current) {
      autoPlayHandledRef.current = true
      handleSpeak()
    }
  }, [autoPlay, text, handleSpeak])

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      autoPlayHandledRef.current = false

      if (isSpeaking || isLoading) {
        stopRef.current?.()
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
        setIsSpeaking(false)
        setIsLoading(false)
      } else {
        handleSpeak()
      }
    },
    [isSpeaking, isLoading, handleSpeak]
  )

  const speakingIcons = [
    <svg key="pause-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
      <path
        fill="#ad9c5f"
        d="M10.25 5.5v13a1.75 1.75 0 0 1-1.75 1.75h-3a1.75 1.75 0 0 1-1.75-1.75v-13A1.76 1.76 0 0 1 5.5 3.75h3a1.75 1.75 0 0 1 1.75 1.75m10 0v13a1.75 1.75 0 0 1-1.75 1.75h-3a1.75 1.75 0 0 1-1.75-1.75v-13a1.76 1.76 0 0 1 1.75-1.75h3a1.75 1.75 0 0 1 1.75 1.75"
      />
    </svg>
  ]

  return (
    <div ref={divRef} onClick={handleClick}>
      {isLoading && (
        <div className="flex items-center justify-center w-5 h-5 text-orange-400">
          <SpinnerIcon size="sm" />
        </div>
      )}
      {!isLoading &&
        (isSpeaking ? (
          <div style={{ display: "block" }}>{speakingIcons[speakingIconIndex]}</div>
        ) : (
          <svg fill="none" height="22" viewBox="0 0 24 24" width="22" xmlns="http://www.w3.org/2000/svg"><path clipRule="evenodd" d="m12 22c5.5228 0 10-4.4772 10-10 0-5.52285-4.4772-10-10-10-5.52285 0-10 4.47715-10 10 0 5.5228 4.47715 10 10 10zm-1.3065-6.1542 4.7202-2.7868c.7817-.4616.7817-1.6564 0-2.118l-4.7202-2.78681c-.75979-.44858-1.6935.13528-1.6935 1.05897v5.57364c0 .9237.93371 1.5076 1.6935 1.059z" fill="#ad9c5f" fillRule="evenodd"/></svg>
        ))}
    </div>
  )
}
